import React, { useCallback } from 'react';
import { User, CreditCard, Users, GraduationCap, LogOut, BookOpen } from 'lucide-react';
import { useAuth } from '@soap/modules';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import type { SettingsTile } from '../../config/settingsTiles';
import { scheduleLogoutFromHomeHash } from '../../../constants/postLogoutReturnUrl';

const iconFor: Record<string, React.ReactNode> = {
  profile: <User className="h-8 w-8 text-action-blue" />,
  billing: <CreditCard className="h-8 w-8 text-action-blue" />,
  'users-hub': <Users className="h-8 w-8 text-action-blue" />,
  'education-system': <GraduationCap className="h-8 w-8 text-action-blue" />,
  'external-resources': <BookOpen className="h-8 w-8 text-action-blue" />
};

type SettingsTilesLandingProps = {
  visibleTiles: SettingsTile[];
};

export function SettingsTilesLanding({ visibleTiles }: SettingsTilesLandingProps) {
  const maintenance = typeof window !== 'undefined' && window.LYW?.maintenance === true;
  const { authReady, authEnabled, isAuthenticated } = useAuth('settings-tiles-landing');

  const onLogoutClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (maintenance || !authReady || !authEnabled || !isAuthenticated) return;
      scheduleLogoutFromHomeHash();
    },
    [authEnabled, authReady, isAuthenticated, maintenance]
  );

  return (
    <div>
      <p className="text-gray-600 mb-6 max-w-2xl">
        Choose an area to manage. Your profile and billing are always available; other tiles depend on your plan and
        role.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visibleTiles.map((tile) => (
          <a key={tile.id} href={tile.href} className="block no-underline">
            <Card className="h-full transition-all hover:shadow-md hover:border-action-blue/40 cursor-pointer border-2 border-transparent">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-action-blue/10 p-2">{iconFor[tile.id] ?? null}</div>
                  <div>
                    <CardTitle className="text-lg">{tile.label}</CardTitle>
                    <CardDescription className="mt-1">{tile.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <span className="text-sm text-action-blue font-medium">Open</span>
              </CardContent>
            </Card>
          </a>
        ))}
        <a
          href="#/logout"
          className="block no-underline sm:col-span-2 lg:col-span-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-blue focus-visible:ring-offset-2 rounded-lg"
          onClick={onLogoutClick}
          aria-disabled={maintenance || !authReady || !authEnabled || !isAuthenticated}
        >
          <Card
            className={`h-full transition-all border-2 border-transparent ${
              maintenance || !authReady || !authEnabled || !isAuthenticated
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:shadow-md hover:border-gray-300 cursor-pointer'
            }`}
          >
            <CardHeader className="py-4 px-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-gray-100 p-2">
                  <LogOut className="h-6 w-6 text-gray-700" />
                </div>
                <div>
                  <CardTitle className="text-base">Log out</CardTitle>
                  <CardDescription className="mt-0.5 text-sm">Sign out on this device</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </a>
      </div>
    </div>
  );
}
